import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { queryQueueProps } from "src/interfaces";

interface TicketQueuesState {
  items: queryQueueProps[];
  unreadCount: number;
  page: number;
  hasMore: boolean;
}

const initialState: TicketQueuesState = {
  items: [],
  unreadCount: 0,
  page: 1,
  hasMore: true,
};

export const queuesSlice = createSlice({
  name: "ticketQueues",
  initialState,
  reducers: {
    // ✅ Handles both first load & next page
    setTicketQueus: (
      state,
      action: PayloadAction<{ data: queryQueueProps[]; page: number; hasMore: boolean }>
    ) => {
      if (action.payload.page === 1) {
        // first page (refresh)
        state.items = action.payload.data;
      } else {
        // next page (append)
        const existingIds = new Set(state.items.map((item) => item.id));
        const newItems = action.payload.data.filter(
          (item) => !existingIds.has(item.id)
        );
        state.items = [...state.items, ...newItems];
      }

      state.page = action.payload.page;
      state.hasMore = action.payload.hasMore;
    },

    // ✅ unread comes directly from backend (total count)
    setUnreadTicketQueCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },

    // ✅ in case we add manually
    incrementUnread: (state) => {
      state.unreadCount += 1;
    },

    decrementUnread: (state) => {
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    },

    // ✅ clears everything
    removeTicketQueus: () => initialState,

    // ✅ remove single by query_id
    removeSingleTicketQueue: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(
        (item) => item.query_id !== action.payload
      );
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    },

    // ✅ reset only pagination (not items/unread)
    resetPagination: (state) => {
      state.page = 1;
      state.hasMore = true;
    },
  },
});

export const {
  setTicketQueus,
  setUnreadTicketQueCount,
  incrementUnread,
  decrementUnread,
  removeTicketQueus,
  removeSingleTicketQueue,
  resetPagination,
} = queuesSlice.actions;

export const selectTicketQueus = (state: RootState) => state.ticketQueues.items;
export const selectUnreadTicketQueCount = (state: RootState) =>
  state.ticketQueues.unreadCount;
export const selectTicketQueuePage = (state: RootState) => state.ticketQueues.page;
export const selectTicketQueueHasMore = (state: RootState) => state.ticketQueues.hasMore;

export default queuesSlice.reducer;
